import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/theme/ThemeContext";
import { hasRole, canAccessApp } from "@/guards/accessControl";
import { APP_REGISTRY, type NavSection } from "@/registry/appRegistry";
import { icons, type LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, Sun, Moon, LogOut, CircleAlert } from "lucide-react";
import { useRemoteNav } from "@/hooks/useRemoteNav";
import { useState } from "react";

/* ─── Icon resolver ─── */
function resolveIcon(name?: string): LucideIcon | null {
  if (!name) return null;
  return (icons as Record<string, LucideIcon>)[name] ?? null;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const visibleApps = APP_REGISTRY.filter((app) => canAccessApp(user, app.requiredRoles));

  // Find the active app based on URL
  const activeApp = visibleApps.find((app) => location.pathname.startsWith(app.basePath)) || visibleApps[0];

  const { navItems, subNavSections, error: navError } = useRemoteNav(activeApp);

  // Get the path relative to the active app's basePath
  const relativePath = activeApp
    ? location.pathname.slice(activeApp.basePath.length).replace(/^\//, "")
    : "";

  // Filter nav items by role
  const visibleNavItems = navItems.filter(
    (item) => !item.requiredRole || hasRole(user, item.requiredRole)
  );

  // Find active sub-nav section
  const activeSubNav: NavSection | null =
    subNavSections.find((section) =>
      section.matchPaths.some(
        (p) => relativePath === p || relativePath.startsWith(p + "/")
      )
    ) || null;

  // Check if a nav item is active
  const isNavActive = (itemPath: string) => {
    if (itemPath === "" && relativePath === "") return true;
    if (itemPath === "") return false;
    return relativePath === itemPath || relativePath.startsWith(itemPath + "/");
  };

  // Check if a sub-nav tab is active
  const isSubNavActive = (tabPath: string) => {
    // Exact match for most tabs
    if (relativePath === tabPath) return true;
    // Allow prefix match for detail pages (e.g. config-viewer/sites/:name)
    if (
      tabPath === "config-viewer/sites" &&
      relativePath.startsWith("config-viewer/sites/") &&
      !relativePath.includes("/new") &&
      !relativePath.includes("/modify")
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* ─── Sidebar ─── */}
      <aside
        className={`fixed top-0 left-0 h-full z-50 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-200 ${
          sidebarCollapsed ? "w-16" : "w-56"
        }`}
      >
        {/* Platform branding */}
        <div className="flex items-center h-14 px-3 border-b border-gray-200 dark:border-gray-700">
          {!sidebarCollapsed && (
            <Link
              to="/"
              className="text-base font-bold text-brand-navy dark:text-white truncate"
            >
              Canopy
            </Link>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              sidebarCollapsed ? "mx-auto" : "ml-auto"
            }`}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* App list */}
        <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
          {visibleApps.map((app) => {
            const Icon = resolveIcon(app.icon);
            const isActive = activeApp?.id === app.id;
            return (
              <Link
                key={app.id}
                to={`${app.basePath}${app.defaultRoute ? "/" + app.defaultRoute : ""}`}
                title={app.description}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-brand-navy/10 text-brand-navy dark:bg-brand-sky/20 dark:text-brand-sky"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
                {!sidebarCollapsed && <span className="truncate">{app.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom section: theme + user + logout */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? <Sun className="h-4 w-4 flex-shrink-0" /> : <Moon className="h-4 w-4 flex-shrink-0" />}
            {!sidebarCollapsed && <span>{isDark ? "Light Mode" : "Dark Mode"}</span>}
          </button>

          {!sidebarCollapsed && user && (
            <div className="px-3 py-1">
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
            </div>
          )}

          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors"
            title="Logout"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* ─── Main content area ─── */}
      <div
        className={`flex-1 flex flex-col transition-all duration-200 ${
          sidebarCollapsed ? "ml-16" : "ml-56"
        }`}
      >
        {/* Nav load error: shown only when we have no usable nav to fall back on */}
        {activeApp && navError && visibleNavItems.length === 0 && (
          <header
            role="alert"
            aria-live="polite"
            className="sticky top-0 z-40 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/50"
          >
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center h-14 gap-3">
                <CircleAlert className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-700 dark:text-red-300">
                  Navigation for {activeApp.name} failed to load.
                </span>
                <button
                  onClick={() => window.location.reload()}
                  className="ml-auto text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 underline"
                >
                  Refresh
                </button>
              </div>
            </div>
          </header>
        )}

        {/* Top navigation bar (app-specific nav) */}
        {activeApp && visibleNavItems.length > 0 && (
          <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700/60 transition-colors">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center h-14">
                {/* App name + nav items */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(() => {
                      const AppIcon = resolveIcon(activeApp.icon);
                      return AppIcon ? (
                        <AppIcon className="h-5 w-5 text-brand-navy dark:text-brand-sky" />
                      ) : null;
                    })()}
                    <span className="text-base font-semibold text-gray-900 dark:text-white">
                      {activeApp.name}
                    </span>
                  </div>

                  {/* Primary nav items */}
                  <nav className="flex items-center gap-1">
                    {visibleNavItems.map((item) => {
                      const Icon = resolveIcon(item.icon);
                      const active = isNavActive(item.path);
                      const fullPath = `${activeApp.basePath}${item.path ? "/" + item.path : ""}`;
                      return (
                        <Link
                          key={item.path}
                          to={fullPath}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            active
                              ? "text-brand-navy dark:text-brand-sky bg-brand-navy/10 dark:bg-brand-sky/20"
                              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          {Icon && <Icon className="h-4 w-4" />}
                          {item.label}
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                {/* Right: org label */}
                <div className="ml-auto flex items-center gap-3">
                  <div className="text-xs font-medium text-gray-400 dark:text-gray-500">
                    Target RWE
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Sub-navigation bar (section-specific tabs) */}
        {activeSubNav && activeSubNav.tabs.length > 1 && (
          <nav className="z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700/60">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-1 -mb-px">
                {activeSubNav.tabs.map((tab) => {
                  const Icon = resolveIcon(tab.icon);
                  const active = isSubNavActive(tab.path);
                  const fullPath = `${activeApp!.basePath}/${tab.path}`;
                  return (
                    <Link
                      key={tab.path}
                      to={fullPath}
                      className={`flex items-center gap-1.5 px-3 py-3 border-b-2 text-sm font-medium transition-colors ${
                        active
                          ? "border-brand-navy dark:border-brand-sky text-gray-900 dark:text-white"
                          : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                    >
                      {Icon && <Icon className="h-4 w-4" />}
                      {tab.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>
        )}

        {/* Child app content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
